import { ref } from 'vue';
import { useStore } from '@/store';
import { UserState } from '@/composables/User';
import { getOperatorById } from '@/components/flows/operators/registry';
import {
  checkIfServiceNeedToBeVerified,
  getNotConfiguredServices,
} from '@/composables/shop/MyShopParameters';
import type { ServiceParameter } from '@/types/store-parameters-types';

export interface ServiceVerificationResult {
  canProceed: boolean;
  notConfiguredServices: ServiceParameter[];
}

/**
 * Composable for verifying required services before adding operators
 * Checks if services like Email, SMS, Push Notification are properly configured
 */
export function useServiceVerification() {
  const store = useStore();
  const isVerifying = ref(false);

  /**
   * Get the shop ID from the active shop
   */
  const getShopId = (): number => UserState.activeShop?.id ?? 0;

  /**
   * Get required services for an operator
   * First checks the operator's requiredServices property, then falls back to legacy check
   */
  const getRequiredServicesForOperator = (operatorId: string): string[] => {
    const operator = getOperatorById(operatorId);

    // Use operator's requiredServices if defined
    if (operator?.requiredServices?.length) {
      return operator.requiredServices;
    }

    // Fallback: check if this operator needs service verification using legacy function
    if (checkIfServiceNeedToBeVerified(operatorId)) {
      return [operatorId];
    }

    return [];
  };

  /**
   * Verify if all required services are configured for an operator
   * @param operatorId - The operator ID to check (e.g., 'boxsendemail')
   * @param existingOperatorIds - Optional list of existing operator IDs in the flow (for bulk checks)
   * @returns ServiceVerificationResult with canProceed and notConfiguredServices
   */
  const verifyServicesForOperator = async (
    operatorId: string,
    existingOperatorIds: string[] = [],
  ): Promise<ServiceVerificationResult> => {
    const shopId = getShopId();
    if (!shopId) {
      return { canProceed: true, notConfiguredServices: [] };
    }

    // Check if this operator needs service verification
    const needsVerification = checkIfServiceNeedToBeVerified(operatorId);

    if (!needsVerification) {
      return { canProceed: true, notConfiguredServices: [] };
    }

    isVerifying.value = true;

    try {
      // Build list of operator IDs that need verification
      // getNotConfiguredServices expects operator IDs like 'boxsendemail', not service names
      const operatorsToCheck = [operatorId, ...existingOperatorIds.filter((id) => checkIfServiceNeedToBeVerified(id))];
      const uniqueOperators = [...new Set(operatorsToCheck)];

      // Get not configured services
      const notConfiguredServices = await getNotConfiguredServices(shopId, uniqueOperators);

      return {
        canProceed: notConfiguredServices.length === 0,
        notConfiguredServices,
      };
    } finally {
      isVerifying.value = false;
    }
  };

  /**
   * Show the service configuration modal
   * @param notConfiguredServices - Services that need to be configured
   * @param onSuccess - Callback to execute after successful configuration
   */
  const showServiceConfigurationModal = (
    notConfiguredServices: ServiceParameter[],
    onSuccess: () => void | Promise<void>,
  ) => {
    store.commit('general/setNotConfiguredServices', notConfiguredServices);
    store.commit('general/setIsServicesParametersModalVisible', true);
    store.commit('general/setServiceParameterSuccessAction', onSuccess);
  };

  /**
   * Verify services and show modal if needed
   * This is a convenience method that combines verification and modal display
   * @param operatorId - The operator ID to check
   * @param onSuccess - Callback to execute if services are configured or after configuration
   * @param existingOperatorIds - Optional list of existing operator IDs in the flow
   * @returns true if can proceed immediately, false if modal was shown
   */
  const verifyAndShowModalIfNeeded = async (
    operatorId: string,
    onSuccess: () => void | Promise<void>,
    existingOperatorIds: string[] = [],
  ): Promise<boolean> => {
    const result = await verifyServicesForOperator(operatorId, existingOperatorIds);

    if (result.canProceed) {
      return true;
    }

    showServiceConfigurationModal(result.notConfiguredServices, onSuccess);
    return false;
  };

  return {
    isVerifying,
    verifyServicesForOperator,
    showServiceConfigurationModal,
    verifyAndShowModalIfNeeded,
    getRequiredServicesForOperator,
  };
}
